import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as api } from '@mesa-labs/mesa-api';
import { useTimezoneSelection } from '@mesa-labs/mesa-ui';
import { DateTime } from 'luxon';

export type PartnerIntegrationsState = {
  partner?: api.Partners;
  periodization?: api.TimeSeriesPeriodization;
  beginningAt?: api.ISODateString;
  endingAt?: api.ISODateString;
  period1BeginningAt?: api.ISODateString;
  period1EndingAt?: api.ISODateString;
  period2BeginningAt?: api.ISODateString;
  period2EndingAt?: api.ISODateString;
  vendorsTotals?: readonly (string | undefined)[];
  clientsTotals?: readonly (string | undefined)[];
  filesFileType?: string;
  filesPartnerId?: api.Partners;
};

export const initialState: PartnerIntegrationsState = {
  partner: undefined,
  periodization: api.TimeSeriesPeriodization.DAY,
  beginningAt: useTimezoneSelection(DateTime.now().minus({ month: 1 }).startOf('day')).toISO(),
  endingAt: useTimezoneSelection(DateTime.now().endOf('day')).toISO(),
  period1BeginningAt: undefined,
  period1EndingAt: undefined,
  period2BeginningAt: undefined,
  period2EndingAt: undefined,
  vendorsTotals: undefined,
  clientsTotals: undefined,
  filesFileType: undefined,
  filesPartnerId: undefined,
};

const partnerIntegrations = createSlice({
  name: 'partnerIntegrations',
  initialState,
  reducers: {
    updatePartner(state: PartnerIntegrationsState, action: PayloadAction<api.Partners | undefined>) {
      state.partner = action.payload;
    },
    updatePeriodization(state: PartnerIntegrationsState, action: PayloadAction<api.TimeSeriesPeriodization>) {
      state.periodization = action.payload;
    },
    updateBeginningAt(state: PartnerIntegrationsState, action: PayloadAction<string | undefined>) {
      state.beginningAt = action.payload;
    },
    updateEndingAt(state: PartnerIntegrationsState, action: PayloadAction<string | undefined>) {
      state.endingAt = action.payload;
    },
    updatePeriod1BeginningAt(state: PartnerIntegrationsState, action: PayloadAction<string | undefined>) {
      state.period1BeginningAt = action.payload;
    },
    updatePeriod1EndingAt(state: PartnerIntegrationsState, action: PayloadAction<string | undefined>) {
      state.period1EndingAt = action.payload;
    },
    updatePeriod2BeginningAt(state: PartnerIntegrationsState, action: PayloadAction<string | undefined>) {
      state.period2BeginningAt = action.payload;
    },
    updatePeriod2EndingAt(state: PartnerIntegrationsState, action: PayloadAction<string | undefined>) {
      state.period2EndingAt = action.payload;
    },
    updateVendorsTotals(state: PartnerIntegrationsState, action: PayloadAction<readonly (string | undefined)[] | undefined>) {
      state.vendorsTotals = action.payload;
    },
    updateClientsTotals(state: PartnerIntegrationsState, action: PayloadAction<readonly (string | undefined)[] | undefined>) {
      state.clientsTotals = action.payload;
    },
    updateFilesFileType(state: PartnerIntegrationsState, action: PayloadAction<string | undefined>) {
      state.filesFileType = action.payload;
    },
    updateFilesPartnerId(state: PartnerIntegrationsState, action: PayloadAction<api.Partners | undefined>) {
      state.filesPartnerId = action.payload;
    },
  },
});

export const {
  updatePartner,
  updatePeriodization,
  updateBeginningAt,
  updateEndingAt,
  updatePeriod1BeginningAt,
  updatePeriod1EndingAt,
  updatePeriod2BeginningAt,
  updatePeriod2EndingAt,
  updateVendorsTotals,
  updateClientsTotals,
  updateFilesFileType,
  updateFilesPartnerId,
} = partnerIntegrations.actions;
export default partnerIntegrations.reducer;
